import { useSearchParams } from 'react-router-dom'
import { withZod } from '@remix-validated-form/with-zod'
import { useEffect, useState } from 'react'
import { Validator } from 'remix-validated-form'
import { z } from 'zod'
import { graphql } from 'app/generated/gql'
import { User } from 'app/types/entities'
import { gqlQuery, gqlMutation } from 'app/lib/graphqlClient'
import { toUserPosition } from 'app/lib/userPosition'
import { logError } from 'lib/logging'

const ACTIVATE_ACCOUNT_QUERY = graphql(`
  query ActivateAccount($token: String!, $email: String!) {
    activateAccount(token: $token, email: $email) {
      allowGoogle
      code
      email
      name
      phone
      position
    }
  }
`)

const SET_PASSWORD_MUTATION = graphql(`
  mutation ActivateSetPassword($token: String!, $email: String!, $password: String!, $clientType: ClientType!) {
    setPassword(token: $token, email: $email, password: $password, clientType: $clientType) {
      accessToken
    }
  }
`)

// The activation screen collects name, phone, position and title. setPassword
// signs the user in (it sets the auth cookie), then updateMe persists those
// profile fields on the now-authenticated user (TRK-6072). position is a
// display label here; toUserPosition converts it to the UserPosition enum.
const UPDATE_ME_MUTATION = graphql(`
  mutation ActivateUpdateMe($input: UpdateUserInput!) {
    updateMe(input: $input) {
      id
    }
  }
`)

type LoaderData =
  | { type: 'error'; error: string }
  | { type: 'invalid-invitation' }
  | {
      type: 'activate'
      email: string
      code: string
      allowGoogle: boolean
      name?: string
      phone?: string
      position?: string
    }
  | { type: 'debug-success' }

type ActionData = {
  success: boolean
  error?: string
}

export const POSITION_OPTIONS = [
  { label: '', value: '' },
  { label: 'Executive', value: 'Executive' },
  { label: 'Consultant', value: 'Consultant' },
  { label: 'Dentist', value: 'Dentist' },
  { label: 'Hygienist', value: 'Hygienist' },
  { label: 'Manager', value: 'Manager' },
  { label: 'Treatment Coordinator', value: 'Treatment Coordinator' },
  { label: 'Administrator', value: 'Administrator' },
  { label: 'Other', value: 'Other' },
]

export const PRIVACY_POLICY_URL =
  'https://app.termly.io/policy-viewer/policy.html?policyUUID=84ed68d7-d676-472c-9974-d35b3a4861a5'
export const TERMS_OF_SERVICE_URL =
  'https://app.termly.io/policy-viewer/policy.html?policyUUID=de7d508f-ec3f-468c-96d8-bbdb2eed61e4'
export const GOOGLE_LOGO_URL =
  'https://lh3.googleusercontent.com/COxitqgJr1sJnIDe8-jiKhxDx1FrYbtRHKJ9z_hELisAlapwE9LUPh6fcXIfb5vwpbMl4xl9H9TRFPc5NOO8Sb3VSgIBrfRYvW6cUA'

const baseSchema = z.object({
  name: z.string().min(1, { message: 'Name is required' }),
  phone: z
    .string()
    .optional()
    .refine(
      (val) => {
        if (!val || val.trim() === '') return true
        const phoneRegex = /^\(\d{3}\) \d{3}-\d{4}$/
        return phoneRegex.test(val) || /^\d{10}$/.test(val)
      },
      { message: 'Phone number must be a valid US number' },
    ),
  position: z.string().min(2, { message: 'Position is required' }),
  jobTitle: z.string().optional(),
  email: z.string().email(),
  password: z
    .string()
    .min(8, { message: 'Password must be at least 8 characters' })
    .regex(/[A-Z]/, { message: 'Password must contain at least one uppercase letter' })
    .regex(/[0-9]/, { message: 'Password must contain at least one number' })
    .regex(/[^A-Za-z0-9]/, { message: 'Password must contain at least one special character' }),
  confirmPassword: z.string().min(8),
})

const refinements = (vals: z.infer<typeof baseSchema>, ctx: z.RefinementCtx) => {
  const { confirmPassword, password, jobTitle, position } = vals
  if (confirmPassword !== password) {
    ctx.addIssue({ code: 'custom', message: 'The passwords did not match', path: ['confirmPassword'] })
  }
  if (position === 'Other' && !jobTitle) {
    ctx.addIssue({ code: 'custom', message: 'Please add your position title', path: ['jobTitle'] })
  }
}

export const activateValidator = withZod(baseSchema.extend({ code: z.string().min(1) }).superRefine(refinements))

export type ActivateScreenState =
  | { screen: 'success'; message: string }
  | { screen: 'invalid-invitation' }
  | { screen: 'error'; error: string }
  | {
      screen: 'form'
      email: string
      code: string
      allowGoogle: boolean
      defaultName?: string
      defaultPhone?: string
      defaultPosition?: string
    }

export type UseActivateReturn = {
  state: ActivateScreenState
  position: User['position']
  setPosition: (p: User['position']) => void
  password: string
  setPassword: (p: string) => void
  useGoogle: boolean
  setUseGoogle: (v: boolean) => void
  isSubmitting: boolean
  actionError?: string
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  validator: Validator<any>
  onSubmit: (formData: FormData) => void
}

export function useActivate(): UseActivateReturn {
  const [searchParams] = useSearchParams()
  const [loaderData, setLoaderData] = useState<LoaderData | null>(null)
  const [actionData, setActionData] = useState<ActionData | undefined>()
  const [isSubmitting, setIsSubmitting] = useState(false)

  // Fetch loader data from API on mount
  useEffect(() => {
    const token = searchParams.get('code') || ''
    const email = searchParams.get('email') || ''
    gqlQuery<{
      activateAccount: {
        allowGoogle: boolean
        code: string
        email: string
        name?: string
        phone?: string
        position?: string
      } | null
    }>(ACTIVATE_ACCOUNT_QUERY, {
      token,
      email,
    })
      .then(({ data, error }) => {
        if (error) {
          setLoaderData({ type: 'error', error: error.message })
          return
        }
        const user = data?.activateAccount
        if (!user) {
          setLoaderData({ type: 'invalid-invitation' })
          return
        }
        setLoaderData({
          type: 'activate',
          email: user.email,
          code: user.code,
          allowGoogle: user.allowGoogle,
          name: user.name,
          phone: user.phone,
          position: user.position,
        })
      })
      .catch(() => setLoaderData({ type: 'error', error: 'Failed to load activation data' }))
  }, [])

  const allowGoogle = loaderData?.type === 'activate' ? loaderData.allowGoogle : false
  const [position, setPosition] = useState<User['position']>(
    (loaderData?.type === 'activate' ? loaderData.position : undefined) as User['position'],
  )
  const [password, setPassword] = useState('')
  const [useGoogle, setUseGoogle] = useState(false)

  // Update allowGoogle-dependent state once loader data arrives
  useEffect(() => {
    if (loaderData?.type === 'activate') {
      setPosition(loaderData.position as User['position'])
      setUseGoogle(loaderData.allowGoogle)
    }
  }, [loaderData])

  function onSubmit(formData: FormData) {
    setIsSubmitting(true)
    setActionData(undefined)
    const token = searchParams.get('code') || ''
    const email = searchParams.get('email') || ''
    const password = formData.get('password') as string
    const name = (formData.get('name') as string | null)?.trim() || undefined
    const phone = (formData.get('phone') as string | null)?.trim() || undefined
    const position = toUserPosition(formData.get('position'))
    const jobTitle = (formData.get('jobTitle') as string | null)?.trim() || undefined

    gqlMutation(SET_PASSWORD_MUTATION, {
      token,
      email,
      password,
      clientType: 'PWA',
    })
      .then(async ({ error }) => {
        if (error) {
          setActionData({ success: false, error: error.message })
          return
        }
        // setPassword signed the user in (auth cookie set); persist the profile
        // fields the activation screen collected (TRK-6072). A failure here must
        // not block activation: the account is already usable, so log it and
        // still show success. The user can set these later in Settings.
        const input = { name, phone, position, jobTitle }
        const hasProfile = name || phone || position || jobTitle
        if (hasProfile) {
          const { error: updateError } = await gqlMutation(UPDATE_ME_MUTATION, { input })
          if (updateError) {
            logError('Activation updateMe failed', updateError)
          }
        }
        setActionData({ success: true })
      })
      .catch(() => setActionData({ success: false, error: 'An unexpected error occurred' }))
      .finally(() => setIsSubmitting(false))
  }

  let state: ActivateScreenState

  if (!loaderData) {
    // Still loading — show a neutral loading state (form screen with empty values)
    state = { screen: 'form', email: '', code: '', allowGoogle: false }
  } else if (loaderData.type === 'debug-success' || actionData?.success) {
    state = { screen: 'success', message: 'You now have access to Trackable. Sign in to get started.' }
  } else if (loaderData.type === 'invalid-invitation') {
    state = { screen: 'invalid-invitation' }
  } else if (loaderData.type === 'error') {
    state = { screen: 'error', error: loaderData.error }
  } else {
    state = {
      screen: 'form',
      email: loaderData.email,
      code: loaderData.code,
      allowGoogle: loaderData.allowGoogle,
      defaultName: loaderData.name,
      defaultPhone: loaderData.phone,
      defaultPosition: loaderData.position,
    }
  }

  return {
    state,
    position,
    setPosition,
    password,
    setPassword,
    useGoogle,
    setUseGoogle,
    isSubmitting,
    actionError: actionData?.error,
    validator: activateValidator,
    onSubmit,
  }
}
